import React, { useMemo } from 'react';

import { Box, Container } from '@mui/material';
import { verifyEmail } from '@uplift/api';
import { useCurrentuser } from '@uplift/contexts';
import { useIsMobile, useL10n } from '@uplift/hooks';
import { ProviderMedicalRole } from '@uplift/types';
import { MainLayoutContainer, ProviderLayout, UpliftThemeProvider, useAlert } from '@uplift/ui-v2';

import { ProviderOnboardingDashboard } from '../onboarding-dashboard/ProviderOnboardingDashboard';
import { DashboardJumpNav, DashboardTabEnum } from './components/dashboard-jump-nav.component';
import { PaymentsBasic } from './payments/basic/payments';
import { PaymentsMSO } from './payments/mso/payments';
import { useDashboardStyles } from './styles';
import { ToDos } from './to-dos/to-dos.main';
import { TodaysSessions } from './todays-sessions/todays-sessions.main';
import { useHistory, useLocation } from 'react-router';
import { useQueryClient } from '@tanstack/react-query';
import { useProviderPublicProfiles } from '@uplift/queries';
import { SharePublicProfileUrl } from './share-public-profile-url/share-public-profile-url.main';

export const DashboardPage: React.FC = () => {
  const me = useCurrentuser();
  const l10n = useL10n();
  const snackbar = useAlert();
  const { classes } = useDashboardStyles();
  const { search, pathname } = useLocation();
  const history = useHistory();
  const queryClient = useQueryClient();
  const { data: providerPublicProfiles = [] } = useProviderPublicProfiles(me.id);
  const providerPublicProfile = providerPublicProfiles[0];
  // explicitly checking === false so it's false even when it hasn't fully loaded
  const isMobile = useIsMobile('sm');
  const isNotFullTime = me?.fullTime === false;
  const isPsychiatrist = me.medicalRole === ProviderMedicalRole.psychiatrist;

  const showPayouts = useMemo(() => {
    if (!isPsychiatrist && isNotFullTime) {
      if (!me.rules?.msoGroup) {
        return true;
      }
    }

    return false;
  }, [ isNotFullTime, isPsychiatrist, me.rules?.msoGroup ]);

  const showJumpNav = isMobile && showPayouts;

  React.useEffect(() => {
    const params = new URLSearchParams(search);

    if (!params.get('email_verification_code')) {
      return;
    }

    const runVerify = async (code: string) => {
      try {
        await verifyEmail(code);
        await queryClient.invalidateQueries([ 'allActionItems' ]);

        snackbar.success(<span dangerouslySetInnerHTML={{ __html: l10n.dashboard.emailVerification.verified }} />);
      } catch (err) {
        if (err.code === 'ECONNABORTED') {
          return;
        }
      } finally {
        params.delete('email_verification_code');
        history.replace({ search: params.toString() });
      }
    };

    runVerify(params.get('email_verification_code'));
  }, [ l10n.dashboard.emailVerification.verified, pathname, search, snackbar ]);

  return (
    <ProviderLayout navigationVariant={showJumpNav ? 'clear' : 'outlined'}>
      <UpliftThemeProvider>
        {showJumpNav && <DashboardJumpNav />}
        <MainLayoutContainer noContainerPadding={isMobile}>
          <ProviderOnboardingDashboard />
          <Container disableGutters>
            <Box className={classes.container}>
              <Box className={classes.grid}>
                <Box className={classes.stack}>
                  <div id={DashboardTabEnum.todaysSessions}>
                    <TodaysSessions />
                  </div>
                  <div id={DashboardTabEnum.toDos}>
                    <ToDos />
                  </div>
                </Box>
                {(showPayouts || providerPublicProfile) && (
                  <Box className={classes.stack}>
                    {showPayouts && (
                      <div id={DashboardTabEnum.payments}>
                        {me.rules?.msoPayout ? <PaymentsMSO /> : <PaymentsBasic />}
                      </div>
                    )}
                    {providerPublicProfile && (
                      <div id={DashboardTabEnum.sharePublicProfileUrl}>
                        <SharePublicProfileUrl profile={providerPublicProfile} payoutsShowing={showPayouts} />
                      </div>
                    )}
                  </Box>
                )}
              </Box>
            </Box>
          </Container>
        </MainLayoutContainer>
      </UpliftThemeProvider>
    </ProviderLayout>
  );
};
